import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type CSSProperties, type RefObject } from 'react';
import { insightsApi } from '../../api/insights';
import type { CardLookupDto, ScanTelemetryEvent } from '../../api/types';

// Neon lime from the iOS scanner (SnapmonPalette.positive).
const GREEN = '#ABFF0E';

interface Reveal {
  tMs: number;
  cardId: string;
}

function formatPrice(marketPrice: string | null, currency: string | null): string {
  if (!marketPrice) return '—';
  const n = parseFloat(marketPrice);
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${currency || ''}${n.toFixed(2)}`;
  }
}

/**
 * Reconstructs the in-app scanner "reveal" over a recorded session clip.
 *
 * Telemetry `tMs` is ms-since-session-start and the video recorder starts at
 * the same t=0, so a `kind:"reveal"` event maps directly to a playback offset.
 * We watch the <video>'s clock and, once playback passes a reveal, spring the
 * matched card's price + `RAW` label in (styled like RevealOverlay.swift) and
 * keep it up until the next reveal (bulk scans) or the clip ends.
 */
export function ScanRevealOverlay({
  telemetry,
  videoRef,
}: {
  telemetry: ScanTelemetryEvent[] | null;
  videoRef: RefObject<HTMLVideoElement | null>;
}) {
  const reveals: Reveal[] = useMemo(
    () =>
      (telemetry ?? [])
        .filter((e) => e.kind === 'reveal' && !!e.cardId)
        .map((e) => ({ tMs: e.tMs, cardId: e.cardId as string }))
        .sort((a, b) => a.tMs - b.tMs),
    [telemetry],
  );

  const uniqueIds = useMemo(() => Array.from(new Set(reveals.map((r) => r.cardId))), [reveals]);

  const cardQueries = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ['card', id],
      queryFn: () => insightsApi.card(id),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const cardById = useMemo(() => {
    const m = new Map<string, CardLookupDto>();
    uniqueIds.forEach((id, i) => {
      const data = cardQueries[i]?.data;
      if (data) m.set(id, data);
    });
    return m;
  }, [uniqueIds, cardQueries]);

  // Active reveal = last one whose tMs has been reached by the playhead.
  const [activeIdx, setActiveIdx] = useState(-1);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reveals.length === 0) {
      setActiveIdx(-1);
      return;
    }
    const update = () => {
      // The recorder stops right at the reveal, so the final reveal's tMs can
      // land past the clip's last frame — once playback has ended, count every
      // reveal as reached so that closing reveal still shows.
      const tMs = video.ended ? Number.POSITIVE_INFINITY : video.currentTime * 1000;
      let idx = -1;
      for (let i = 0; i < reveals.length; i++) {
        if (reveals[i].tMs <= tMs) idx = i;
        else break;
      }
      setActiveIdx(idx);
    };
    update();
    video.addEventListener('timeupdate', update);
    video.addEventListener('seeking', update);
    video.addEventListener('seeked', update);
    video.addEventListener('ended', update);
    return () => {
      video.removeEventListener('timeupdate', update);
      video.removeEventListener('seeking', update);
      video.removeEventListener('seeked', update);
      video.removeEventListener('ended', update);
    };
  }, [videoRef, reveals]);

  if (activeIdx < 0) return null;
  const active = reveals[activeIdx];
  const card = cardById.get(active.cardId);
  if (!card) return null;

  const price = formatPrice(card.marketPrice, card.currency);

  return (
    <div style={overlayWrap} aria-hidden>
      {/* keyed on cardId so the spring-in re-fires when the active card changes */}
      <div key={active.cardId} style={panel}>
        {card.imageSmallUrl && <img src={card.imageSmallUrl} alt="" style={thumb} />}
        <div style={info}>
          <div style={nameRow}>
            <span style={nameText}>{card.name}</span>
            <span style={rawPill}>RAW</span>
          </div>
          <div style={idText}>
            {card.id}
            {card.number ? ` · #${card.number}` : ''}
          </div>
          <div style={priceText}>{price}</div>
        </div>
      </div>
    </div>
  );
}

const overlayWrap: CSSProperties = {
  // Lower-third so the panel never covers the card being scanned.
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  padding: 12,
  pointerEvents: 'none',
};

// Legible dark panel: card art + name + id + green price. Solid fills only —
// no text-stroke (the stacked-stroke look was unreadable over camera footage).
const panel: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  maxWidth: '100%',
  padding: '10px 14px 10px 10px',
  borderRadius: 14,
  background: 'rgba(12, 12, 12, 0.82)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  border: `1px solid rgba(171, 255, 14, 0.35)`,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
  animation: 'scan-reveal-in 0.24s cubic-bezier(0.34, 1.56, 0.64, 1) both',
};

const thumb: CSSProperties = {
  height: 84,
  width: 'auto',
  borderRadius: 6,
  display: 'block',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
  flexShrink: 0,
};

const info: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
};

const nameRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const nameText: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 700,
  fontSize: 16,
  lineHeight: 1.15,
  color: '#fff',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const rawPill: CSSProperties = {
  flexShrink: 0,
  fontFamily: 'var(--font-mono)',
  fontWeight: 600,
  fontSize: 10,
  letterSpacing: '0.06em',
  color: '#0c0c0c',
  background: GREEN,
  borderRadius: 999,
  padding: '2px 7px',
};

const idText: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  color: 'rgba(255, 255, 255, 0.6)',
};

const priceText: CSSProperties = {
  fontFamily: "'Barlow', var(--font-display)",
  fontWeight: 800,
  fontSize: 26,
  lineHeight: 1,
  color: GREEN,
  textShadow: '0 1px 3px rgba(0, 0, 0, 0.6)',
};
